<script setup lang="ts">
import { computed, onBeforeUnmount } from "vue";
import { NodeViewWrapper } from "@tiptap/vue-3";
import type { NodeViewProps } from "@tiptap/vue-3";
import SvgIcon from "../SvgIcon.vue";
import { getVariableAlignStyle, type VariableAlign } from "./VariableBlock";

const props = defineProps<NodeViewProps>();

const variableDisplay = computed(() => `{{${props.node.attrs.variable}}}`);

const wrapperStyle = computed(() => ({
  width: props.node.attrs.width || "200px",
  height: props.node.attrs.height || "120px",
  ...getVariableAlignStyle((props.node.attrs.align || "none") as VariableAlign),
}));

let activeMouseMove: ((e: MouseEvent) => void) | null = null;
let activeMouseUp: (() => void) | null = null;

function detachResizeListeners() {
  if (activeMouseMove) window.removeEventListener("mousemove", activeMouseMove);
  if (activeMouseUp) window.removeEventListener("mouseup", activeMouseUp);
  activeMouseMove = null;
  activeMouseUp = null;
}

const startResize = (event: MouseEvent) => {
  const startX = event.clientX;
  const startY = event.clientY;
  const startWidth = parseInt(props.node.attrs.width || "200", 10);
  const startHeight = parseInt(props.node.attrs.height || "120", 10);

  activeMouseMove = (moveEvent: MouseEvent) => {
    const deltaX = moveEvent.clientX - startX;
    const deltaY = moveEvent.clientY - startY;
    const newWidth = Math.max(80, startWidth + deltaX);
    const newHeight = Math.max(60, startHeight + deltaY);
    props.updateAttributes({ width: `${newWidth}px`, height: `${newHeight}px` });
  };

  activeMouseUp = () => detachResizeListeners();

  window.addEventListener("mousemove", activeMouseMove);
  window.addEventListener("mouseup", activeMouseUp);
};

onBeforeUnmount(detachResizeListeners);
</script>

<template>
  <NodeViewWrapper as="div" class="variable-image-wrapper" :style="wrapperStyle">
    <div class="variable-image-placeholder" contenteditable="false">
      <div class="placeholder-content">
        <SvgIcon name="upload" class="placeholder-icon w-[24px] h-[24px] min-w-[24px] min-h-[24px]" />
        <span class="placeholder-label">{{ props.node.attrs.label || props.node.attrs.variable }}</span>
        <span class="placeholder-variable-name">{{ variableDisplay }}</span>
      </div>
    </div>
    <span class="resize-handle" @mousedown.prevent="startResize">
      <div class="resize-handle-icon">
        <svg viewBox="0 0 12 12" width="12" height="12">
          <path d="M0,12 L12,0" stroke="currentColor" stroke-width="2" />
          <path d="M8,12 L12,8" stroke="currentColor" stroke-width="2" />
        </svg>
      </div>
    </span>
  </NodeViewWrapper>
</template>

<style scoped lang="scss">
.variable-image-wrapper {
  @apply relative;
}

.variable-image-placeholder {
  @apply w-full h-full border-2 border-dashed border-stroke-grey-color rounded-lg bg-light-grey-color flex items-center justify-center cursor-default select-none transition-colors duration-200;
}

.variable-image-wrapper:has(.resize-handle:hover) .variable-image-placeholder {
  @apply border-medium-grey-color bg-stroke-color;
}

.placeholder-content {
  @apply flex flex-col items-center gap-1 py-3 px-2;
}

.placeholder-icon {
  @apply text-secondary-color opacity-60;
}

.placeholder-label {
  @apply text-[13px] font-semibold text-secondary-color text-center;
}

.placeholder-variable-name {
  @apply text-[10px] font-bold text-secondary-color opacity-50;
}

.resize-handle {
  @apply absolute right-0 bottom-0 w-[14px] h-[14px] cursor-se-resize z-10;
}

.resize-handle-icon {
  @apply absolute right-1 bottom-1 w-3 h-3 cursor-se-resize text-secondary-color opacity-40 transition-opacity duration-200;

  svg {
    @apply block;
  }
}

.resize-handle:hover .resize-handle-icon {
  @apply opacity-100;
}
</style>
